import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DiscordIcon from '@mui/icons-material/Chat';
import TelegramIcon from '@mui/icons-material/Telegram';
import TestIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { alertSettingsService, type TelegramChannel } from '../../services/alertSettingsService';

interface AlertSettingsPanelProps {
  onClose?: () => void;
}

export const AlertSettingsPanel: React.FC<AlertSettingsPanelProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingDiscord, setTestingDiscord] = useState(false);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [telegramChannels, setTelegramChannels] = useState<TelegramChannel[]>([]);

  const [formData, setFormData] = useState({
    discordWebhookEnabled: false,
    discordWebhookUrl: '',
    telegramWebhookEnabled: false,
    telegramBotToken: '',
    telegramChatId: '',
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({
    discordWebhookEnabled: false,
    discordWebhookUrl: '',
    telegramWebhookEnabled: false,
    telegramBotToken: '',
    telegramChatId: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setError(null);
      const data = await alertSettingsService.getSettings();

      const settingsData = {
        discordWebhookEnabled: data.discordWebhookEnabled,
        discordWebhookUrl: data.discordWebhookUrl || '',
        telegramWebhookEnabled: data.telegramWebhookEnabled,
        telegramBotToken: data.telegramBotToken || '',
        telegramChatId: data.telegramChatId || '',
      };

      setFormData(settingsData);
      setOriginalData(settingsData); // Store original values for comparison
    } catch (error) {
      console.error('Failed to load alert settings:', error);
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to check for unsaved changes
  const hasDiscordChanges = () => {
    return (
      formData.discordWebhookEnabled !== originalData.discordWebhookEnabled || formData.discordWebhookUrl !== originalData.discordWebhookUrl
    );
  };

  const hasTelegramChanges = () => {
    return (
      formData.telegramWebhookEnabled !== originalData.telegramWebhookEnabled ||
      formData.telegramBotToken !== originalData.telegramBotToken ||
      formData.telegramChatId !== originalData.telegramChatId
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateData = {
        discordWebhookEnabled: formData.discordWebhookEnabled,
        discordWebhookUrl: formData.discordWebhookEnabled ? formData.discordWebhookUrl : undefined,
        telegramWebhookEnabled: formData.telegramWebhookEnabled,
        telegramBotToken: formData.telegramWebhookEnabled ? formData.telegramBotToken : undefined,
        telegramChatId: formData.telegramWebhookEnabled ? formData.telegramChatId : undefined,
      };

      await alertSettingsService.updateSettings(updateData);

      // Update original data to reflect saved state
      setOriginalData({ ...formData });
      setSuccess('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save alert settings:', error);
      setError('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestDiscord = async () => {
    if (!formData.discordWebhookEnabled || !formData.discordWebhookUrl) {
      setError('Please enable Discord webhooks and provide a webhook URL first.');
      return;
    }

    setTestingDiscord(true);
    setError(null);
    setSuccess(null);

    try {
      await alertSettingsService.sendTestDiscordWebhook();
      setSuccess('Test Discord message sent successfully! Check your Discord channel.');
    } catch (error) {
      console.error('Failed to send test Discord webhook:', error);
      setError('Failed to send test Discord message. Please check your webhook URL.');
    } finally {
      setTestingDiscord(false);
    }
  };

  const handleTestTelegram = async () => {
    if (!formData.telegramWebhookEnabled || !formData.telegramBotToken || !formData.telegramChatId) {
      setError('Please enable Telegram webhooks and provide both bot token and chat ID.');
      return;
    }

    setTestingTelegram(true);
    setError(null);
    setSuccess(null);

    try {
      await alertSettingsService.sendTestTelegramWebhook();
      setSuccess('Test Telegram message sent successfully! Check your Telegram chat.');
    } catch (error) {
      console.error('Failed to send test Telegram webhook:', error);
      setError('Failed to send test Telegram message. Please check your bot token and chat ID.');
    } finally {
      setTestingTelegram(false);
    }
  };

  const handleLoadChannels = async () => {
    if (!formData.telegramBotToken.trim()) {
      setError('Please enter a bot token first.');
      return;
    }

    setLoadingChannels(true);
    setError(null);
    setSuccess(null);

    try {
      const channels = await alertSettingsService.getTelegramChannels(formData.telegramBotToken);
      setTelegramChannels(channels);

      if (channels.length === 0) {
        setError('No channels found. Make sure your bot has been added to at least one channel or group and has received messages there.');
      } else {
        setSuccess(`Found ${channels.length} channel(s). Select one from the dropdown below.`);
      }
    } catch (error) {
      console.error('Failed to load Telegram channels:', error);
      setError('Failed to load channels. Please check your bot token and ensure the bot has been added to channels.');
      setTelegramChannels([]);
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleChannelSelect = (channelId: string) => {
    setFormData({ ...formData, telegramChatId: channelId });
    setError(null);
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant='h5' component='h2' gutterBottom>
        Alert Notification Settings
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Configure webhook notifications to receive alerts in Discord and Telegram when new alerts are detected.
      </Typography>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity='success' sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Discord Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display='flex' alignItems='center' gap={1}>
              <DiscordIcon color='primary' />
              <Typography variant='h6'>Discord Notifications</Typography>
              <Chip
                label={formData.discordWebhookEnabled ? 'Enabled' : 'Disabled'}
                color={formData.discordWebhookEnabled ? 'success' : 'default'}
                size='small'
              />
              {hasDiscordChanges() && <Chip label='Unsaved Changes' color='warning' size='small' variant='outlined' />}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.discordWebhookEnabled}
                    onChange={e => setFormData({ ...formData, discordWebhookEnabled: e.target.checked })}
                  />
                }
                label='Enable Discord webhook notifications'
              />

              {formData.discordWebhookEnabled && (
                <>
                  <TextField
                    fullWidth
                    label='Discord Webhook URL'
                    value={formData.discordWebhookUrl}
                    onChange={e => setFormData({ ...formData, discordWebhookUrl: e.target.value })}
                    placeholder='https://discord.com/api/webhooks/...'
                    helperText='Get this URL from your Discord server settings → Integrations → Webhooks'
                  />

                  <Tooltip title={hasDiscordChanges() ? 'Please save your changes before testing' : ''} placement='top'>
                    <span>
                      <Button
                        variant='outlined'
                        startIcon={testingDiscord ? <CircularProgress size={16} /> : <TestIcon />}
                        onClick={handleTestDiscord}
                        disabled={testingDiscord || !formData.discordWebhookUrl || hasDiscordChanges()}
                        sx={{
                          ...(hasDiscordChanges() && {
                            opacity: 0.6,
                            '&.Mui-disabled': {
                              color: 'warning.main',
                              borderColor: 'warning.main',
                            },
                          }),
                        }}
                      >
                        {testingDiscord ? 'Sending Test...' : 'Send Test Message'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* Telegram Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display='flex' alignItems='center' gap={1}>
              <TelegramIcon color='primary' />
              <Typography variant='h6'>Telegram Notifications</Typography>
              <Chip
                label={formData.telegramWebhookEnabled ? 'Enabled' : 'Disabled'}
                color={formData.telegramWebhookEnabled ? 'success' : 'default'}
                size='small'
              />
              {hasTelegramChanges() && <Chip label='Unsaved Changes' color='warning' size='small' variant='outlined' />}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.telegramWebhookEnabled}
                    onChange={e => setFormData({ ...formData, telegramWebhookEnabled: e.target.checked })}
                  />
                }
                label='Enable Telegram webhook notifications'
              />

              {formData.telegramWebhookEnabled && (
                <>
                  <Box>
                    <TextField
                      fullWidth
                      label='Bot Token'
                      value={formData.telegramBotToken}
                      onChange={e => {
                        setFormData({ ...formData, telegramBotToken: e.target.value });
                        // Clear channels when token changes
                        if (telegramChannels.length > 0) {
                          setTelegramChannels([]);
                        }
                      }}
                      placeholder='123456789:ABCdefGHIjklMNOpqrsTUVwxyz'
                      helperText='Get this from @BotFather on Telegram'
                    />

                    {formData.telegramBotToken.trim() && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={loadingChannels ? <CircularProgress size={16} /> : <RefreshIcon />}
                          onClick={handleLoadChannels}
                          disabled={loadingChannels}
                        >
                          {loadingChannels ? 'Loading Channels...' : 'Load Channels'}
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {telegramChannels.length > 0 && (
                    <FormControl fullWidth>
                      <InputLabel>Select Channel</InputLabel>
                      <Select value={formData.telegramChatId} onChange={e => handleChannelSelect(e.target.value)} label='Select Channel'>
                        {telegramChannels.map(channel => (
                          <MenuItem key={channel.id} value={channel.id}>
                            {channel.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Choose the channel where notifications will be sent</FormHelperText>
                    </FormControl>
                  )}

                  <TextField
                    fullWidth
                    label='Chat ID'
                    value={formData.telegramChatId}
                    onChange={e => setFormData({ ...formData, telegramChatId: e.target.value })}
                    placeholder='-1001234567890'
                    helperText='The chat ID where notifications will be sent (auto-filled when selecting from channels above)'
                  />

                  <Tooltip title={hasTelegramChanges() ? 'Please save your changes before testing' : ''} placement='top'>
                    <span>
                      <Button
                        variant='outlined'
                        startIcon={testingTelegram ? <CircularProgress size={16} /> : <TestIcon />}
                        onClick={handleTestTelegram}
                        disabled={testingTelegram || !formData.telegramBotToken || !formData.telegramChatId || hasTelegramChanges()}
                        sx={{
                          ...(hasTelegramChanges() && {
                            opacity: 0.6,
                            '&.Mui-disabled': {
                              color: 'warning.main',
                              borderColor: 'warning.main',
                            },
                          }),
                        }}
                      >
                        {testingTelegram ? 'Sending Test...' : 'Send Test Message'}
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Divider />

        {/* Action Buttons */}
        <Box display='flex' justifyContent='flex-end' gap={2}>
          {onClose && (
            <Button variant='outlined' onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button
            variant='contained'
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
